import { useMemo, useState } from 'react';
import { DataTable } from '@/components/ui/data-table/data-table';
import { useTicketBreakdown } from '@/hooks/use-analytics-v2';
import type { TicketBreakdownLevel, TicketBreakdownRow } from '@/types/analyticsV2';
import { BreakdownBreadcrumb, type BreakdownPath } from './breakdown-breadcrumb';
import { breakdownColumns, canDrill } from './breakdown-columns';

const HINT: Partial<Record<TicketBreakdownLevel, string>> = {
  category: 'Select a category to see its subcategories',
  subcategory: 'Select a subcategory to see its intents',
};

const levelOf = ({ category, subcategory }: BreakdownPath): TicketBreakdownLevel => {
  if (subcategory != null) return 'intent';
  return category != null ? 'subcategory' : 'category';
};

interface TicketBreakdownSectionProps {
  fromDate: string;
  toDate: string;
  /** Super-admin only: render metrics for this account instead of the caller's own. */
  overrideAccountId?: number;
}

/** The Ticket tab's breakdown: tickets by category, then a category's subcategories, then a subcategory's intents. */
export function TicketBreakdownSection({ fromDate, toDate, overrideAccountId }: TicketBreakdownSectionProps) {
  const [path, setPath] = useState<BreakdownPath>({});
  const by = levelOf(path);
  const { data, isLoading, isError } = useTicketBreakdown(fromDate, toDate, { by, ...path }, overrideAccountId);
  const columns = useMemo(() => breakdownColumns(by), [by]);

  const open = (row: TicketBreakdownRow) => {
    if (!canDrill(by, row)) return;
    setPath(by === 'category' ? { category: row.key! } : { ...path, subcategory: row.key! });
  };

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-baseline justify-between gap-3'>
        <h2 className='text-[12px] text-gray-800 font-medium'>Tickets by {by}</h2>
        {HINT[by] ? <span className='text-[12px] text-gray-700'>{HINT[by]}</span> : null}
      </div>
      <div className='rounded-3xl border border-gray-300 bg-white p-2'>
        {path.category != null ? <BreakdownBreadcrumb path={path} onNavigate={setPath} /> : null}
        <DataTable
          columns={columns}
          data={data ?? []}
          enableSorting={false}
          enablePagination={false}
          isLoading={isLoading}
          onRowClick={by === 'intent' ? undefined : open}
          getRowClassName={(row) => (canDrill(by, row) ? '' : 'cursor-default')}
          emptyMessage={isError ? 'Failed to load the breakdown.' : 'No tickets in this range.'}
        />
      </div>
    </div>
  );
}
